import React, { FunctionComponent } from "react";
import { ICardElementWithChildren } from "backend/model/model";
import TextElement from "reportengine/TextElement";
import Text from "reportengine/Text";
import Report from "reportengine/Report";
import Slide from "reportengine/Slide";
import { createReport, saveBlob } from "reportengine/createReport";
import reportContextToRequest from "./reportContextToRequest";
import DomElement from "reportengine/DomElement";
import ProgressModal from "design/components/progress/ProgressModal";
import Portion from "reportengine/Portion";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import flattenHierarchy from "backend/flattenHierarchy";
import KPISlide from "./slides/KPISlide";
import OverallPerformanceCard from "design/components/cards/OverallPerformanceCard";
import ElementOverviewDonutCard from "design/components/cards/ElementOverviewDonutCard";
import KPIPerformanceTable from "./slides/KPIPerformanceTable";
import InitiativePerformanceTable from "./slides/InitiativePerformanceTable";
import InitiativeSlide from "./slides/InitiativeSlide";
import { useSelectedReportContext } from "state/SelectedReportContext";
import useElement from "backend/hooks/elements/useElement";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";

const StrategyReport = (
  props: React.PropsWithChildren<{
    item: ICardElementWithChildren;
    filetype: string;
    onClose?: () => void;
  }>
) => {
  const { item, filetype } = props;
  const {
    isLoading: isChildrenLoading,
    data: children,
    isPreviousData,
    isRefetching,
  } = useHierarchy(item.id);
  const { data: topElement } = useElement(item.top_element_id);
  const [status, setStatus] = React.useState(t("reportGen.Generating Report").toString());
  const [progress, setProgres] = React.useState(0);
  const [blob, setBlob] = React.useState<null | Blob>(null);
  const reportSelection = useSelectedReportContext();
  const ctx = useAppContext();
  const translatedName = getTranslatedAttribute(item, "Name", ctx.language);
  const translatedScorecardName = getTranslatedAttribute(topElement, "Name", ctx.language);
  const cnf = conf.getStrategyReportSettings()

  /**
   * This flag is only for testing pages faster
   */
  const disableDetailsPages = false;

  const kpis = React.useMemo(() => {
    return flattenHierarchy(children).filter((item) => item.typename === "KPI");
  }, [children]);

  const initiatives = React.useMemo(() => {
    return flattenHierarchy(children).filter(
      (item) => item.typename === "Initiative"
    );
  }, [children]);

  if (isChildrenLoading || isPreviousData || isRefetching) {
    return (
      <ProgressModal
        isOpen
        onClose={() => {
          if (props.onClose) {
            props.onClose();
          }
        }}
        isComplete={!!blob}
        cancelClickHandler={props.onClose}
        completedClickHandler={() => {
          if (blob) {
            saveBlob(`${item.name}.${filetype}`, blob);
          }
        }}
        downloadText={t("reportGen.Download")}
        progress={progress}
        statusText={t("reportGen.Initializing...")}
      />
    );
  }
  return (
    <div>
      <Report
        template="/templates/report_en.pptx"
        onNothingToRender={() => {
          setStatus(t("reportGen.Nothing to render"));
        }}
        onProgress={(value) => {
          setProgres(value);
          console.log("slide progress ", value);
        }}
        onReadyToRender={async (report) => {
          setProgres(95);
          const blob = await createReport(
            await reportContextToRequest(report, item.symbol, filetype)
          );
          setStatus(t("reportGen.Report ready"));
          setBlob(blob);
          setProgres(100);
        }}
      >
        <Slide index={0}>
          <TextElement slideElement="title">
            {t("strategyReport." + reportSelection.report?.reportType?.name) || t("strategyReport.Strategy Report")}
          </TextElement>
          <TextElement slideElement="subtitle">{translatedName}</TextElement>
          <TextElement slideElement="footer">
            {item.period?.name || ""}
          </TextElement>
        </Slide>
        <Slide index={1}>
          {(() => {
            if (!cnf.hideOverallPerformanceSummary) {
              return (
                <TextElement slideElement="heading_1_text">
                  {t("strategyReport.Overall Performance Summary")}
                </TextElement>
              );
            } else {
              return null;
            }
          })()}
          {(() => {
            if (!cnf.hideKPIPerformanceSummary) {
              return (
                <TextElement slideElement="heading_2_text">
                  {t("strategyReport.KPI Performance Summary")}
                </TextElement>
              );
            } else {
              return null;
            }
          })()}
          {(() => {
            if (!cnf.hideInitiativePerformanceSummary) {
              return (
                <TextElement slideElement="heading_3_text">
                  {t("strategyReport.Initiative Performance Summary")}
                </TextElement>
              );
            } else {
              return null;
            }
          })()}
          <Text slideElement="period">
            <Portion paragraphIndex={0} portionIndex={0}>
              {item.period?.name || ""}
            </Portion>
          </Text>
          <TextElement slideElement="footer">{translatedScorecardName}</TextElement>
        </Slide>
        {(() => {
            if (!cnf.hideOverallPerformanceSummary) {
              return (
                <Slide index={2}>
                  <TextElement slideElement="section_number">01</TextElement>
                  <TextElement slideElement="title">
                    {t("strategyReport.Overall Performance Summary")}
                  </TextElement>
                  <TextElement slideElement="footer">{translatedScorecardName}</TextElement>
                </Slide>
              );
            } else {
              return null;
            }
        })()}
        {(() => {
            if (!cnf.hideOverallPerformanceSummary) {
              return (
                <Slide index={3}>
                  <TextElement slideElement="header">
                    {t("strategyReport.Performance Report")}
                  </TextElement>
                  <TextElement slideElement="title">
                    {t("strategyReport.Overall Performance Summary")}
                  </TextElement>
                  <Text slideElement="period">
                    <Portion paragraphIndex={0} portionIndex={0}>
                      {item.period?.name || ""}
                    </Portion>
                  </Text>
                  <TextElement slideElement="footer">{translatedScorecardName}</TextElement>
                  {conf.getOverviewDashboardSettings().hideOverallPerformance ===
                  true ? null : (
                    <TextElement slideElement="overall_pefrormance">
                      {t("strategyReport.Overall Performance YTD")}
                    </TextElement>
                  )}
                  {cnf.hideKPIPerformanceSummary ===
                  true ? null : (
                    <TextElement slideElement="kpi_performance_overview">
                      {t("strategyReport.KPI Performance Overview YTD")}
                    </TextElement>
                  )}
                  {cnf.hideInitiativePerformanceSummary ===
                  true ? null : (
                    <TextElement slideElement="corporate_overall_performance">
                      {t("strategyReport.Initiative Performance Overview")}
                    </TextElement>
                  )}
                  {conf.getOverviewDashboardSettings().hideOverallPerformance ===
                  true ? null : (
                    <DomElement
                      typename="image"
                      selector=".chart"
                      slideElement="overall_performance_chart"
                      transform={{
                        width: 70,
                        height: 70,
                      }}
                    >
                    <div style={{ width: "500px", height: "300px" }}>
                      <OverallPerformanceCard
                        key="PERF_W_YTD"
                        titleKey="PERF_W_YTD_prettyvalue"
                        element={item}
                        disableAnimation
                        periodName={item.period?.name || ""}
                      />
                    </div>
                  </DomElement>
                )}
                {cnf.hideKPIPerformanceSummary ===
                true ? null : (
                  <DomElement
                      typename="image"
                      selector=".chart"
                      slideElement="kpi_performance_chart"
                      transform={{
                        x: -90 + 20,
                        y: -90,
                        height: 180,
                        width: 180,
                      }}
                    >
                    {" "}
                    <ElementOverviewDonutCard
                      title={t("strategyReport.KPI Overview")}
                      data={children!}
                      typenames={["KPI"]}
                      disableAnimation
                      periodName={item.period?.name || ""}
                    />{" "}
                  </DomElement>
                )}
                {cnf.hideInitiativePerformanceSummary ===
                true ? null : (
                  <DomElement
                      typename="image"
                      selector=".chart"
                      slideElement="corporate_overall_performance_chart"
                      transform={{
                        x: -90,
                        y: -90,
                        height: 180,
                        width: 180,
                      }}
                    >
                    {" "}
                    <ElementOverviewDonutCard
                      title={t("strategyReport.Initiative Overview")}
                      data={children!}
                      typenames={["Initiative"]}
                      disableAnimation
                      periodName={item.period?.name || ""}
                    />{" "}
                  </DomElement>
                )}
                </Slide>
              );
            } else {
              return null;
            }
          })()}

        {(() => {
            if (!cnf.hideKPIPerformanceSummary) {
              return (
                <Slide index={4}>
                  <TextElement slideElement="title">
                    {t("strategyReport.KPI Performance Summary")}
                  </TextElement>
                  <TextElement slideElement="footer">{translatedScorecardName}</TextElement>
                </Slide>
              );
            } else {
              return null;
            }
        })()}

        {(() => {
            if (!cnf.hideKPIPerformanceTable) {
              return (
                <KPIPerformanceTable kpis={kpis} report={item} 
                  title={t("strategyReport.KPI Performance Summary")} 
                  translatedScorecardName={translatedScorecardName} />
              );
            } else {
              return null;
            }
        })()}

        {!cnf.hideKPIPerformanceDetails && !disableDetailsPages &&
          kpis.map((kpi) => {
            return <KPISlide item={kpi} translatedScorecardName={translatedScorecardName}/>;
          })}

        {(() => {
            if (!cnf.hideInitiativePerformanceSummary) {
              return (
                <Slide index={7}>
                  <TextElement slideElement="section_number">03</TextElement>
                  <TextElement slideElement="title">
                    {t("strategyReport.Initiative Performance Summary")}
                  </TextElement>
                  <TextElement slideElement="footer">{translatedScorecardName}</TextElement>
                </Slide>
              );
            } else {
              return null;
            }
        })()}

        {(() => {
            if (!cnf.hideInitiativePerformanceTable) {
              return (
                <InitiativePerformanceTable items={initiatives} report={item}
                title={t("strategyReport.Initiative Performance Summary")} 
                translatedScorecardName={translatedScorecardName} />        
                    );
            } else {
              return null;
            }
        })()}

        {!cnf.hideInitiativePerformanceDetails && !disableDetailsPages &&
          initiatives.map((initiative) => {
            return <InitiativeSlide item={initiative} translatedScorecardName={translatedScorecardName}/>;
          })}

        <Slide index={10}>
          <TextElement slideElement="subtitle">{translatedName}</TextElement>
          <TextElement slideElement="title">{t("strategyReport.Performance Report")}</TextElement>
        </Slide>
      </Report>
      <ProgressModal
        isOpen
        onClose={() => {
          if (props.onClose) {
            props.onClose();
          }
        }}
        isComplete={!!blob}
        cancelClickHandler={props.onClose}
        completedClickHandler={() => {
          if (blob) {
            saveBlob(`${item.name}.${filetype}`, blob);
          }
        }}
        downloadText={t("reportGen.Download")}
        progress={progress}
        statusText={status}
      />
    </div>
  );
};

export default StrategyReport;
